import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-60px" },
};

const slideLeft = {
  initial: { opacity: 0, x: -40 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: "-100px" },
};

const slideRight = {
  initial: { opacity: 0, x: 40 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: "-100px" },
};

const timeline = [
  {
    year: '2021',
    title: 'Founded in Nairobi',
    description: 'BEKAALI ENTERPRISES LIMITED was established with a clear vision: to bridge the trade gap between East Africa and China. Starting with a small office and a dedicated team, our founder set out to build a company that would redefine cross-continental trade.',
  },
  {
    year: '2022',
    title: 'First Major Procurement Contract',
    description: 'Secured our first large-scale procurement contract — supplying construction machinery to a major infrastructure project in Kenya. This milestone validated our model and established our reputation for reliability and quality.',
  },
  {
    year: '2023',
    title: 'Expansion into Logistics & China Operations',
    description: 'Launched our integrated logistics division and opened our first China office in Guangzhou — adding freight forwarding, supply chain management, and direct supplier relationships at the source to our service portfolio.',
  },
  {
    year: '2024',
    title: 'Pan-African Reach',
    description: 'Expanded operations to serve clients across Tanzania, Uganda, Rwanda, Ethiopia, and the DRC. Our partner network grew to over 200 verified suppliers across China\'s manufacturing hubs, with 3,000+ successful shipments delivered.',
  },
  {
    year: '2025',
    title: 'Dar es Salaam Logistics Hub',
    description: 'Established a strategic logistics hub in Dar es Salaam, Tanzania, reducing delivery times by 40% for East African clients and strengthening our distribution capabilities across the region.',
  },
  {
    year: '2026',
    title: 'Next Chapter',
    description: 'Continuing to innovate with digital procurement platforms, expanded industry coverage, and deeper integration across the China-Africa trade corridor. Serving 30+ countries with a growing network of global partners.',
  },
];

const values = [
  {
    title: 'Integrity',
    description: 'We uphold honesty, transparency, and accountability in every partnership and every decision.',
    icon: 'ri-shield-star-line',
  },
  {
    title: 'Excellence',
    description: 'We pursue the highest standards in quality, service, and operational performance.',
    icon: 'ri-medal-2-line',
  },
  {
    title: 'Customer Focus',
    description: 'We place our clients at the center of everything we do, delivering solutions tailored to their unique needs.',
    icon: 'ri-user-heart-line',
  },
  {
    title: 'Innovation',
    description: 'We embrace modern technologies and smarter ways of working to improve efficiency and create lasting value.',
    icon: 'ri-lightbulb-flash-line',
  },
  {
    title: 'Reliability',
    description: 'We deliver on our promises with consistency, professionalism, and precision.',
    icon: 'ri-timer-line',
  },
  {
    title: 'Collaboration',
    description: 'We build strong relationships with clients, suppliers, and partners to achieve shared success.',
    icon: 'ri-group-line',
  },
];

const processSteps = [
  { step: '01', title: 'Understand', description: 'We begin by understanding your business needs, project requirements, and operational objectives.' },
  { step: '02', title: 'Source', description: 'We identify trusted manufacturers and suppliers who meet the required quality, specifications, and budget.' },
  { step: '03', title: 'Procure', description: 'We manage procurement, negotiations, quality assurance, and order coordination with complete transparency.' },
  { step: '04', title: 'Move', description: 'Our logistics team coordinates freight forwarding, customs clearance, warehousing, and transportation.' },
  { step: '05', title: 'Deliver', description: 'Products are delivered safely, on time, and in accordance with your project schedule.' },
];

const teamMembers = [
  {
    name: 'Benjamin K. Aali',
    role: 'Founder & Chief Executive Officer',
    bio: 'With over 15 years of cross-border trade experience, Benjamin founded BEKAALI with a vision to transform how East African businesses access Chinese manufacturing. His deep understanding of both markets and commitment to excellence drive the company\'s strategic direction.',
    initials: 'BA',
  },
  {
    name: 'Dr. Lin Wei',
    role: 'Managing Director — China Operations',
    bio: 'Dr. Lin oversees our extensive China-based operations including supplier relationships, quality control, and logistics coordination. With a PhD in Supply Chain Management and 12 years at leading Chinese manufacturing firms, she ensures our clients receive world-class products and service.',
    initials: 'LW',
  },
  {
    name: 'Grace Muthoni',
    role: 'Director of Procurement & Quality',
    bio: 'Grace leads our procurement strategy and quality assurance programs. A certified supply chain professional with 10 years of experience across Africa and Asia, she has built the rigorous supplier verification and quality management systems that define BEKAALI\'s reputation.',
    initials: 'GM',
  },
  {
    name: 'Ahmed Hassan',
    role: 'Director of Logistics & Freight',
    bio: 'Ahmed manages our end-to-end logistics operations including warehousing, freight forwarding, and customs clearance. His expertise in multi-modal transportation and African trade corridors ensures timely, cost-effective delivery for every shipment.',
    initials: 'AH',
  },
  {
    name: 'Zhang Wei',
    role: 'Head of Supplier Relations',
    bio: 'Based in our Guangzhou office, Zhang Wei manages relationships with over 200 verified Chinese manufacturers. His deep industry knowledge and negotiation expertise consistently deliver optimal pricing and quality for our clients.',
    initials: 'ZW',
  },
  {
    name: 'Faith Wanjiru',
    role: 'Director of Client Partnerships',
    bio: 'Faith leads our client relationship team across East Africa, ensuring every client receives personalized attention and solutions tailored to their specific industry and business objectives. Her background in international business development spans 8 years.',
    initials: 'FW',
  },
];

export default function About() {
  const [activeValue, setActiveValue] = useState<number | null>(null);

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">

        {/* Page Header — redesigned to match homepage dark-navy + gold style */}
        <section className="relative w-full min-h-[400px] md:min-h-[480px] bg-primary-900 overflow-hidden flex items-center">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Epic%20cinematic%20wide%20shot%20of%20modern%20African%20corporate%20headquarters%20with%20African%20and%20Chinese%20business%20professionals%20walking%20through%20grand%20glass%20atrium%2C%20warm%20golden%20light%20flooding%20through%20large%20windows%2C%20confident%20diverse%20team%2C%20premium%20architectural%20photography%20with%20dramatic%20depth%2C%20professional%20atmosphere&width=1600&height=900&seq=about-hero-v2&orientation=landscape"
              alt="BEKAALI — Building Tomorrow's Business, Today"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-primary-950/85"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/30"></div>
          </div>

          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-16 md:py-20">
            <div className="max-w-4xl">
              {/* Label pill */}
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-block px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border border-white/20 text-white/80 bg-white/5 backdrop-blur-sm mb-8"
              >
                About Bekaali
              </motion.span>

              {/* Bold uppercase headline with gold accent */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="text-4xl sm:text-5xl md:text-7xl font-heading font-bold text-white leading-[1.05] mb-5 uppercase tracking-tight"
              >
                Building Tomorrow&apos;s
                <br />
                <span className="text-accent-400">Business, Today</span>
              </motion.h1>

              {/* One-liner */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-base md:text-lg text-white/60 max-w-2xl mb-10 leading-relaxed"
              >
                A trusted trading, procurement, logistics, and supply chain solutions company — connecting East African businesses with global markets through reliable partnerships and operational excellence since 2021.
              </motion.p>

              {/* Stats row */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="flex flex-wrap items-center gap-8 md:gap-12 pt-8 border-t border-white/10"
              >
                {[
                  { number: '5+', label: 'Years Experience' },
                  { number: '200+', label: 'Global Partners' },
                  { number: '30+', label: 'Countries Served' },
                  { number: '3,000+', label: 'Shipments Delivered' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-2">
                    <span className="text-2xl md:text-3xl font-heading font-bold text-accent-400">{stat.number}</span>
                    <span className="text-xs text-white/40 max-w-[80px] leading-tight uppercase tracking-wider">{stat.label}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Introduction</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Building Tomorrow&apos;s Business, Today</h2>
            </motion.div>
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-4 text-sm text-foreground-600 leading-relaxed"
            >
              <p>
                Global trade is driven by trust, efficiency, and the ability to deliver without compromise. Today&apos;s businesses operate in a fast-moving world where every decision impacts performance, profitability, and growth.
              </p>
              <p>
                Success depends on reliable procurement, efficient logistics, and strong partnerships that keep operations moving without interruption. At <strong>BEKAALI ENTERPRISES LIMITED</strong>, we help organizations navigate these challenges with integrated trading, procurement, freight forwarding, and supply chain solutions designed to simplify operations and create lasting value.
              </p>
              <p>
                Whether sourcing quality products, coordinating international shipments, or managing end-to-end logistics, we are committed to delivering excellence at every stage of the journey. Because behind every successful business is a partner that makes success possible.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Our Purpose */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-primary-900 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary-500/5 rounded-full blur-[100px]"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <motion.span
              {...fadeInUp}
              transition={{ duration: 0.6 }}
              className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4"
            >
              Our Purpose
            </motion.span>
            <motion.p
              {...fadeInUp}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-2xl md:text-3xl font-heading font-bold text-white leading-relaxed"
            >
              To connect businesses with opportunities through reliable procurement, seamless logistics, and trusted partnerships that drive sustainable growth.
            </motion.p>
          </div>
        </section>

        {/* Who We Are */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Who We Are</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Connecting Businesses. Creating Opportunities.</h2>
            </motion.div>
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-4 text-sm text-foreground-600 leading-relaxed"
            >
              <p>
                <strong>BEKAALI ENTERPRISES LIMITED</strong> is a trusted trading, procurement, logistics, and supply chain solutions company committed to helping businesses source smarter, move faster, and grow confidently.
              </p>
              <p>
                We connect clients with reliable suppliers, quality products, and efficient logistics networks, delivering integrated solutions that simplify procurement and ensure every project is executed with professionalism and precision.
              </p>
              <p>
                From international sourcing and freight forwarding to customs clearance and final delivery, we provide end-to-end support that enables organizations to focus on growth while we manage the complexities of the supply chain.
              </p>
            </motion.div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-background-50 border border-background-200/70 rounded-2xl p-8 hover:border-accent-200/50 transition-all duration-400"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-5">
                  <i className="ri-star-line text-xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">Our Strength</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  Instead of selling products, we deliver complete business solutions. Our ability to combine procurement, logistics, freight forwarding, and supply chain management under one trusted partner enables clients to operate more efficiently while reducing complexity and risk.
                </p>
              </motion.div>
              <motion.div
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-background-50 border border-background-200/70 rounded-2xl p-8 hover:border-accent-200/50 transition-all duration-400"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-200 text-secondary-600 mb-5">
                  <i className="ri-heart-line text-xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">Our Commitment</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  We are committed to reliability, transparency, and precision at every stage of the supply chain — helping our clients focus on growth while we manage the complexities of trading, procurement, and logistics.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Vision, Mission & Values</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Reaching New Horizons</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 mb-16">
              <motion.div
                {...slideLeft}
                transition={{ duration: 0.6 }}
                className="bg-background-100 border border-background-200/70 rounded-2xl p-8 md:p-10 hover:border-accent-200/50 transition-all duration-400"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-5">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">Our Vision</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  To bridge geographical boundaries by connecting global demand with reliable supply, delivering seamless procurement, logistics, and supply chain solutions that enable businesses, industries, and communities to thrive.
                </p>
              </motion.div>
              <motion.div
                {...slideRight}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-background-100 border border-background-200/70 rounded-2xl p-8 md:p-10 hover:border-accent-200/50 transition-all duration-400"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-200 text-secondary-600 mb-5">
                  <i className="ri-focus-2-line text-xl"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">Our Mission</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  To create value at every stage of the supply chain by delivering reliable trading, procurement, logistics, and freight solutions that drive efficiency, foster lasting partnerships, and empower our clients to achieve sustainable growth.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="text-center mb-14 md:mb-18">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">What Drives Us</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Our Core Values</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Six principles that guide every decision we make and every relationship we build.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onMouseEnter={() => setActiveValue(index)}
                  onMouseLeave={() => setActiveValue(null)}
                  className={`group relative bg-background-50 border rounded-2xl p-6 md:p-8 transition-all duration-400 cursor-default ${
                    activeValue === index
                      ? 'border-accent-300 -translate-y-1'
                      : 'border-background-200/70'
                  }`}
                >
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-5 transition-all duration-400 ${
                    activeValue === index ? 'bg-accent-500 text-white' : 'bg-accent-100 text-accent-600'
                  }`}>
                    <i className={`${value.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2.5">{value.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-primary-900 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent-500/5 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary-500/5 rounded-full blur-[100px]"></div>
          </div>
          <div className="relative z-10 max-w-6xl mx-auto">
            <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4">Our Process</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4">From Source to Success</h2>
              <p className="text-white/50 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                At BEKAALI ENTERPRISES LIMITED, every successful project follows a proven process built on precision, collaboration, and accountability. By managing every stage of the supply chain, we simplify complexity, minimize risk, and ensure our clients receive reliable, end-to-end solutions from sourcing to final delivery.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
              {processSteps.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: idx * 0.12 }}
                  className={`group relative p-8 border border-white/5 hover:bg-white/[0.03] transition-all duration-400 ${idx < 3 ? 'lg:border-b' : ''} ${idx % 3 !== 2 ? 'lg:border-r' : ''}`}
                >
                  <div className="text-5xl font-heading font-bold text-white/5 mb-6 group-hover:text-accent-500/10 transition-colors duration-400">{item.step}</div>
                  <div className="relative -mt-10">
                    <h3 className="text-lg font-heading font-semibold text-white mb-2 group-hover:text-accent-400 transition-colors duration-300">{item.title}</h3>
                    <p className="text-sm text-white/40 group-hover:text-white/60 transition-colors duration-300 leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Company History Timeline */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="text-center mb-14 md:mb-18">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Our Journey</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Company History</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                From a small office in Nairobi to a trusted partner for businesses across Africa — our journey of growth, innovation, and commitment.
              </p>
            </motion.div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2"></div>

              <div className="space-y-0">
                {timeline.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`relative flex items-start gap-6 md:gap-0 pb-10 md:pb-14 last:pb-0 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-accent-500 rounded-full -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50"></div>
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <span className="inline-block text-xs font-bold text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-3">
                        {item.year}
                      </span>
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                    </div>
                    <div className="hidden md:block md:w-1/2"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="text-center mb-14 md:mb-18">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Our People</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Leadership Team</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Meet the experienced professionals driving BEKAALI&apos;s mission across Africa and Asia.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-accent-200/50 hover:-translate-y-1 transition-all duration-400"
                >
                  <div className="w-16 h-16 rounded-2xl bg-accent-100 flex items-center justify-center mb-5 group-hover:bg-accent-500 transition-colors duration-400">
                    <span className="text-xl font-heading font-bold text-accent-600 group-hover:text-white transition-colors duration-400">
                      {member.initials}
                    </span>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-xs text-accent-600 font-medium mb-3 leading-snug">{member.role}</p>
                  <p className="text-sm text-foreground-500 leading-relaxed">{member.bio}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-primary-900">
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.7 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">
              Let&apos;s Build the Future Together
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm md:text-base leading-relaxed mb-8">
              Every successful partnership begins with a conversation. Whether you&apos;re sourcing quality products, managing complex logistics, or seeking a dependable procurement partner, BEKAALI ENTERPRISES LIMITED is ready to help you achieve your business objectives with confidence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="px-8 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Get in Touch
              </Link>
              <Link
                to="/products"
                className="px-8 py-3.5 bg-white/10 backdrop-blur-sm text-white font-semibold text-sm rounded-full hover:bg-white/20 border border-white/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Browse Products
              </Link>
            </div>
          </motion.div>
        </section>

      </main>
      <Footer />
    </>
  );
}